<template>
  <div class="flex text-strongest" :class="[isFinalizeStep ? 'flex-col' : 'items-center']">
    <p
      v-if="!isFinalizeStep"
      class="text-sm font-inter tracking-[-.28px] mr-[1.125rem] whitespace-nowrap"
    >
      Your investment
    </p>
    <p v-else class="text-2xl font-medium tracking-[-.48px] mb-3 whitespace-nowrap">
      Your Selection
    </p>
    <div class="flex items-center gap-2">
      <!-- Amount Change -->
      <div class="flex flex-col relative">
        <div
          class="rounded-[100px] flex items-center transition-colors bg-container-bg"
          :class="[
            isFinalizeStep ? 'h-14 p-[.875rem]' : 'h-9 px-1',
            amountError ? 'border border-info' : '',
          ]"
        >
          <Button
            @click="changeInvestmentAmount('decrease')"
            :size="isFinalizeStep ? 'sm' : 'xs'"
            variant="paper-primary"
            :disabled="disableAmountEdit"
          >
            <template #icon>
              <Icon name="Minus" class="h-4 w-4" :stroke-width="3" />
            </template>
          </Button>
          <div class="relative flex items-center" :class="amountWidthClass">
            <span
              v-if="!isEditingAmount"
              class="font-inter font-medium text-center tabular-nums w-full"
              :class="[
                isFinalizeStep ? 'text-lg' : 'text-sm',
                disableAmountEdit ? 'cursor-default' : 'cursor-text',
              ]"
              @click="disableAmountEdit ? undefined : startEditingAmount()"
            >
              ${{ formattedDisplayAmount }}
            </span>
            <input
              v-else
              ref="amountInputRef"
              type="text"
              inputmode="numeric"
              :value="editingAmountValue"
              @beforeinput="blockNonNumericInput"
              @input="handleAmountInput"
              @blur="finishEditingAmount"
              @keydown.enter="finishEditingAmount"
              @keydown.escape="cancelEditingAmount"
              class="font-inter font-medium text-center tabular-nums w-full bg-transparent outline-none whitespace-nowrap"
              :class="isFinalizeStep ? 'text-lg' : 'text-sm'"
            />
          </div>
          <Button
            @click="changeInvestmentAmount('increase')"
            :size="isFinalizeStep ? 'sm' : 'xs'"
            variant="paper-primary"
            :disabled="disableAmountEdit"
          >
            <template #icon>
              <Icon name="Plus" class="h-4 w-4" :stroke-width="3" />
            </template>
          </Button>
        </div>
        <div
          v-if="amountError"
          class="absolute left-0 top-full mt-2 text-info text-xs font-inter whitespace-nowrap flex items-center font-medium"
        >
          <Icon
            name="Asterisk"
            class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-info"
            :stroke-width="3"
          />
          {{ amountError }}
        </div>
      </div>

      <!-- Compounding/Simple Dropdown -->
      <FilterDropdown
        :is-open="activeDropdown === 'compounding'"
        @toggle="toggleDropdown('compounding')"
        @close="closeDropdown"
      >
        <template #trigger="{ toggle }">
          <div
            @click="toggle"
            class="rounded-[100px] bg-container-bg flex items-center cursor-pointer"
            :class="isFinalizeStep ? 'h-14 p-[.875rem]' : 'h-9 pr-1 pl-3'"
          >
            <p
              class="font-inter font-medium pr-3 whitespace-nowrap"
              :class="isFinalizeStep ? 'text-lg' : 'text-sm'"
            >
              {{
                purchaseFlowStore.current_investment?.is_compounded
                  ? "Interest paid at maturity"
                  : "Interest paid monthly"
              }}
            </p>
            <Button
              :size="isFinalizeStep ? 'sm' : 'xs'"
              variant="paper-primary"
              @click.stop="toggle"
            >
              <template #icon>
                <Icon
                  name="ChevronDown"
                  class="h-4 w-4 transition-transform duration-300"
                  :class="{ 'rotate-180': activeDropdown === 'compounding' }"
                  :stroke-width="3"
                />
              </template>
            </Button>
          </div>
        </template>

        <template #default="{ close }">
          <div
            v-for="option in compoundingOptions"
            :key="option.value"
            @click="
              handleCompoundingChange(option.value);
              close();
            "
            class="cursor-pointer bg-container-bg text-strongest rounded-lg px-4 py-3"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-inter text-sm font-medium whitespace-nowrap">{{ option.label }}</p>
                <p
                  v-if="option.description"
                  class="font-inter text-light text-xs mt-0.5 whitespace-nowrap"
                >
                  {{ option.description }}
                </p>
              </div>
              <Icon
                v-if="purchaseFlowStore.current_investment?.is_compounded === option.value"
                name="Check"
                class="h-5 w-5 text-strongest ml-3"
                :stroke-width="2.5"
              />
            </div>
          </div>
        </template>
      </FilterDropdown>

      <!-- Offering Dropdown -->
      <FilterDropdown
        v-if="!isTermAndYieldStep"
        :is-open="activeDropdown === 'offering'"
        @toggle="toggleDropdown('offering')"
        @close="closeDropdown"
      >
        <template #trigger="{ toggle }">
          <div
            @click="toggle"
            :class="isFinalizeStep ? 'h-14 p-[.875rem]' : 'h-9 pr-1 pl-3'"
            class="rounded-[100px] bg-container-bg flex items-center cursor-pointer whitespace-nowrap"
          >
            <p
              :class="isFinalizeStep ? 'text-lg' : 'text-sm'"
              class="font-inter font-medium pr-3 whitespace-nowrap"
            >
              {{ getOfferingTermAndInterestRateDisplay(purchaseFlowStore?.selected_offering) }}
            </p>
            <Button
              :size="isFinalizeStep ? 'sm' : 'xs'"
              variant="paper-primary"
              @click.stop="toggle"
            >
              <template #icon>
                <Icon
                  name="ChevronDown"
                  class="h-4 w-4 transition-transform duration-300"
                  :class="{ 'rotate-180': activeDropdown === 'offering' }"
                  :stroke-width="3"
                />
              </template>
            </Button>
          </div>
        </template>

        <template #default="{ close }">
          <div
            v-for="offering in filteredOfferings"
            :key="offering.id"
            @click="
              handleOfferingChange(offering);
              close();
            "
            class="cursor-pointer bg-container-bg text-strongest rounded-lg px-4 py-3 hover:bg-container-bg/80 transition-colors"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-inter text-sm font-medium">
                  {{ getOfferingTermAndInterestRateDisplay(offering) }}
                </p>
                <p class="font-inter text-light text-xs mt-0.5 whitespace-nowrap">
                  {{ offering.type }} offering
                </p>
              </div>
              <Icon
                v-if="purchaseFlowStore.selected_offering?.record_id === offering.record_id"
                name="Check"
                class="h-5 w-5 text-strongest ml-3"
                :stroke-width="2.5"
              />
            </div>
          </div>
        </template>
      </FilterDropdown>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick, onMounted } from "vue";

import { usePurchaseFlowStore, useDataStore } from "@/stores";
import { Button, Icon, FilterDropdown } from "@/components";
import { commaSeparateThousands } from "@/utilities";
import { useStripeCheckout } from "@/composables/useStripeCheckout";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();
const stripeCheckout = useStripeCheckout();

const activeDropdown = ref(null);
const isEditingAmount = ref(false);
const editingAmountValue = ref("");
const previousAmount = ref(null);
const amountInputRef = ref(null);
const boundaryError = ref(null);
let boundaryTimeout = null;

const compoundingOptions = [
  {
    value: false,
    label: "Interest paid monthly",
    description: "Receive interest payments each month",
  },
  {
    value: true,
    label: "Interest paid at maturity",
    description: "Interest compounds and is paid at the end",
  },
];

const props = defineProps({
  isFinalizeStep: {
    type: Boolean,
    default: false,
  },
});
//hi again
// Synchronous local check — used on non-finalize steps to immediately disable
// amount editing when a card was already authorized earlier in the flow.
const cardAuthorized = computed(() => {
  const inv = purchaseFlowStore.current_investment;
  return inv.payment_method === "Card" && !!String(inv.funding_account ?? "").startsWith("pi_");
});

// On the finalize/review step, verify the hold is still active via the backend.
// Starts true if the store already has a PI (avoids a flash of editability before
// the async check resolves), then gets updated to the real backend answer.
const cardOnHold = ref(cardAuthorized.value);

onMounted(async () => {
  if (!props.isFinalizeStep) return;
  const inv = purchaseFlowStore.current_investment;
  if (inv.payment_method !== "Card") return;
  const piId = String(inv.funding_account ?? "").startsWith("pi_") ? inv.funding_account : null;
  if (!piId) return;
  cardOnHold.value = await stripeCheckout.checkHoldStatus(piId);
});

const disableAmountEdit = computed(() =>
  props.isFinalizeStep ? cardOnHold.value : cardAuthorized.value
);

const isTermAndYieldStep = computed(() => {
  return purchaseFlowStore.currentStep?.name === "Term and Yield";
});

const formattedDisplayAmount = computed(() => {
  return commaSeparateThousands(parseAmount(purchaseFlowStore.current_investment?.amount));
});

function showBoundaryError(message) {
  clearTimeout(boundaryTimeout);
  boundaryError.value = message;
  boundaryTimeout = setTimeout(() => {
    boundaryError.value = null;
  }, 3000);
}

const maximumAmount = computed(() => {
  if (!purchaseFlowStore.selected_rollover) return null;
  return Math.floor(purchaseFlowStore.selected_rollover.rollover_available / 1000) * 1000;
});

const minimumAmount = computed(() => {
  if (purchaseFlowStore.selected_rollover) return 1000;

  const offeringType = purchaseFlowStore.selected_offering?.type;

  if (offeringType === "Reg D") {
    return purchaseFlowStore.signedInvestmentStatus?.has_signed_regd ? 1000 : 25000;
  }

  if (offeringType === "S1") {
    if (purchaseFlowStore.selected_offering?.is_short_term) {
      return 1000;
    }
    return purchaseFlowStore.signedInvestmentStatus?.has_signed_s1 ? 1000 : 5000;
  }

  return 1000;
});

const amountError = computed(() => {
  const currentAmount = parseAmount(purchaseFlowStore.current_investment?.amount);
  if (!currentAmount) return boundaryError.value;

  if (currentAmount < minimumAmount.value) {
    return `${formatCurrency(minimumAmount.value)} minimum investment for this offering`;
  }

  if (maximumAmount.value && currentAmount > maximumAmount.value) {
    return `Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`;
  }

  return boundaryError.value;
});

const filteredOfferings = computed(() => {
  const selected = purchaseFlowStore.selected_offering;
  const currentOfferingType = selected?.type;

  if (!currentOfferingType || !dataStore.offerings) return [];

  const selectedTier = selected?.tier;
  const isShortTerm = selected?.is_short_term === true;

  let offerings_filtered = dataStore.offerings
    .filter((offering) => {
      if (offering.type !== currentOfferingType) return false;

      if (isShortTerm ? !offering.is_short_term : offering.is_short_term) return false;

      if (currentOfferingType === "Reg D") {
        if (selectedTier == null) return false;
        return offering.tier === selectedTier;
      }

      return true;
    })
    .sort((a, b) => {
      if (a.bond_term !== b.bond_term) return b.bond_term - a.bond_term;
      return b.interest_rate - a.interest_rate;
    });

  return offerings_filtered;
});

const amountWidthClass = computed(() => {
  const amount = parseInt(
    purchaseFlowStore.current_investment?.amount?.toString().replace(/,/g, "") || "0"
  );

  if (amount >= 1000000) return "w-28";
  if (amount >= 100000) return "w-24";
  return "w-20";
});

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function toggleDropdown(dropdownName) {
  activeDropdown.value = activeDropdown.value === dropdownName ? null : dropdownName;
}

function closeDropdown() {
  activeDropdown.value = null;
}

function parseAmount(value) {
  if (!value) return 0;
  return parseInt(value.toString().replace(/[^0-9]/g, "")) || 0;
}

function startEditingAmount() {
  previousAmount.value = parseAmount(purchaseFlowStore.current_investment?.amount);
  editingAmountValue.value = commaSeparateThousands(previousAmount.value);
  isEditingAmount.value = true;

  nextTick(() => {
    amountInputRef.value?.focus();
    amountInputRef.value?.select();
  });
}

function blockNonNumericInput(event) {
  if (!event.data) return;
  if (/[^0-9]/.test(event.data)) {
    event.preventDefault();
  }
}

function handleAmountInput(event) {
  const numericOnly = event.target.value.replace(/[^0-9]/g, "");
  const numericValue = parseInt(numericOnly) || 0;
  editingAmountValue.value = commaSeparateThousands(numericValue);
}

function finishEditingAmount() {
  if (!isEditingAmount.value) return;

  const newAmount = parseAmount(editingAmountValue.value);

  if (newAmount >= 1000) {
    let roundedAmount = Math.ceil(newAmount / 1000) * 1000;

    // Clamp to min/max bounds
    if (roundedAmount < minimumAmount.value) {
      roundedAmount = minimumAmount.value;
      showBoundaryError(
        `${formatCurrency(minimumAmount.value)} minimum investment for this offering`
      );
    }
    if (maximumAmount.value && roundedAmount > maximumAmount.value) {
      roundedAmount = maximumAmount.value;
      showBoundaryError(`Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`);
    }

    purchaseFlowStore.current_investment.amount = commaSeparateThousands(roundedAmount);
  } else {
    purchaseFlowStore.current_investment.amount = commaSeparateThousands(previousAmount.value);
  }

  isEditingAmount.value = false;
  editingAmountValue.value = "";
  previousAmount.value = null;
}

function cancelEditingAmount() {
  purchaseFlowStore.current_investment.amount = commaSeparateThousands(previousAmount.value);
  isEditingAmount.value = false;
  editingAmountValue.value = "";
  previousAmount.value = null;
}

function changeInvestmentAmount(action) {
  const currentAmount = parseInt(
    purchaseFlowStore.current_investment?.amount?.toString().replace(/,/g, "") || "0"
  );

  if (action === "increase") {
    const newAmount = currentAmount + 1000;
    if (maximumAmount.value && newAmount > maximumAmount.value) {
      showBoundaryError(`Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`);
      return;
    }
    purchaseFlowStore.current_investment.amount = newAmount.toLocaleString();
  } else if (action === "decrease") {
    const newAmount = currentAmount - 1000;
    if (newAmount < minimumAmount.value) {
      showBoundaryError(
        `${formatCurrency(minimumAmount.value)} minimum investment for this offering`
      );
      return;
    }
    purchaseFlowStore.current_investment.amount = newAmount.toLocaleString();
  }
}

function getOfferingTermAndInterestRateDisplay(offering) {
  if (!offering) return "";

  const term =
    offering.bond_term >= 12 ? `${offering.bond_term / 12}-year` : `${offering.bond_term}-month`;

  if (offering.is_short_term) {
    return `${offering.bond_term}-month term · ${offering.interest_rate}% interest`;
  }

  return `${term} term · ${offering.interest_rate}% interest`;
}

function handleCompoundingChange(value) {
  if (purchaseFlowStore.current_investment) {
    purchaseFlowStore.current_investment.is_compounded = value;
  }
}

function handleOfferingChange(offering) {
  purchaseFlowStore.selected_offering = offering;
}
</script>
